import { InputLabel, MenuItem, SelectChangeEvent, useEventCallback } from '@mui/material';
import { memo } from 'react';
import { __MUNICIPALITY_API_URL__ } from 'shell/constants/environment';
import { ItemVM } from 'shell/view-models/item';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { Province } from 'shell/models/province';
import { PmiFormControl } from '../pmi-select/pmi-form-control';
import { PmiSelect } from '../pmi-select';

interface ProvinceListComponentProps {
  disabled?: boolean;
  value: string;
  onChange: (event: SelectChangeEvent<string>, codRegione?: string) => void;
  error?: boolean;
  label?: string;
  id?: string;
  onBootstrap?: boolean;
}

export interface ItemVMProvince extends ItemVM {
  codRegione?: string;
}

const ProvinceListComponent = ({
  disabled,
  onChange,
  value,
  error,
  label = 'Provincia *',
  id = 'province',
  onBootstrap = true,
}: ProvinceListComponentProps): React.ReactElement => {
  const [provinceList] = useHttpFetch<HttpResponseList<Province>, Array<ItemVMProvince>>(
    `${__MUNICIPALITY_API_URL__}/geo/provincia?sort=nome:ASC`,
    {
      options: {
        method: 'GET',
        abortable: true,
        errorCatching: false,
        loading: false,
      },
      onBootstrap,
      transformer: (response) => {
        return response.data.payload.list.map((item) => ({
          id: item.key.codice,
          value: item.nome,
          codRegione: item.codRegione || '',
        }));
      },
    }
  );

  const onChangeSelect = useEventCallback((event: SelectChangeEvent<string>) => {
    const codRegione =
      ((provinceList?.data || []) as Array<ItemVMProvince>).find(
        (item: ItemVMProvince) => item.id === event.target.value
      )?.codRegione || '';

    onChange(event, codRegione);
  });

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} disabled={disabled} id={`${id}-label`}>
        {label}
      </InputLabel>
      <PmiSelect
        labelId={`${id}-label`}
        id={id}
        name={id}
        value={value}
        label={label}
        onChange={onChangeSelect}
        disabled={disabled}
        error={error}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {provinceList?.data
          ? (provinceList?.data as Array<ItemVMProvince>).map((item: ItemVMProvince) => (
              <MenuItem key={item.id} value={item.id}>
                {item.value}
              </MenuItem>
            ))
          : undefined}
      </PmiSelect>
    </PmiFormControl>
  );
};

ProvinceListComponent.displayName = 'Province list component';

const ProvinceList = memo(ProvinceListComponent);

export default ProvinceList as typeof ProvinceListComponent;
